import { motion, useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { BRAND, IMG, waLink } from "@/lib/brand";
import { FaWhatsapp } from "react-icons/fa";
import { Phone, MapPin, Star } from "lucide-react";
import { useT } from "@/lib/i18n";
import { Link } from "react-router-dom";
import { OWNER_ROUTES } from "@/lib/owners.i18n";
import { KeyRound } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Phones get a light portrait clip; data-saver users get only the poster.
const pickHeroMedia = () => {
  const mobile = window.matchMedia("(max-width: 767px)").matches;
  const saveData = navigator.connection?.saveData;
  const poster = `${BACKEND_URL}${mobile ? IMG.heroPosterMobile : IMG.heroPoster}`;
  if (saveData) return { video: undefined, poster };
  return { video: `${BACKEND_URL}${mobile ? IMG.heroVideoMobile : IMG.heroVideo}`, poster };
};


const LangPill = ({ label, delay = 0, t }) => (
  <motion.span
    data-testid={`lang-pill-${label.toLowerCase()}`}
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    className="inline-flex items-center gap-2 bg-navy-700/60 text-gold-light text-xs md:text-sm font-medium px-4 py-2 rounded-full border border-gold/30 backdrop-blur-sm whitespace-nowrap"
  >
    <span className="h-1.5 w-1.5 rounded-full bg-gold" />
    {t(`lang.${label}`)}
  </motion.span>
);

function Counter({ to = 200, suffix = "+", duration = 1.6 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-30%" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - start) / (duration * 1000));
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * to));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);
  return (
    <span ref={ref} className="font-serif text-5xl text-gold tabular-nums inline-block min-w-[4ch]">
      {n}
      {suffix}
    </span>
  );
}

export default function Hero() {
  const sectionRef = useRef(null);
  const { t, lang } = useT();
  const [media] = useState(pickHeroMedia);

  return (
    <section
      ref={sectionRef}
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden pt-28 pb-20 flex items-center grain"
    >
      {/* Background video */}
      <div className="absolute inset-0 z-0">
        <video
          data-testid="hero-video"
          className="h-full w-full object-cover opacity-70"
          src={media.video}
          poster={media.poster}
          autoPlay={!!media.video}
          muted
          loop
          playsInline
          preload={media.video ? "auto" : "none"}
          aria-hidden="true"
        >
          {/* Fallback for users / bots that cannot play video */}
          <img
            src={IMG.hero}
            alt="Warsaw skyline"
            className="h-full w-full object-cover opacity-70"
          />
        </video>
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1733] via-[#0B1733]/85 to-[#0B1733]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1733] via-transparent to-transparent" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="lg:col-span-8"
          >
            <div className="flex flex-wrap items-center gap-3 mb-8">
              <div className="inline-flex items-center gap-2 bg-gold/10 border border-gold/30 rounded-full px-4 py-1.5">
                <MapPin className="h-3.5 w-3.5 text-gold" />
                <span className="text-xs tracking-[0.2em] text-gold-light uppercase">
                  {t("hero.location")}
                </span>
              </div>
              <div
                data-testid="hero-live-badge"
                className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-400/30 rounded-full px-3 py-1.5"
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75" />
                  <span className="relative h-2 w-2 rounded-full bg-emerald-400" />
                </span>
                <span className="text-[11px] tracking-wide text-emerald-200 uppercase font-medium">
                  {t("hero.live")}
                </span>
              </div>
            </div>

            <h1 className="font-serif text-[2.5rem] leading-[1.05] sm:text-5xl lg:text-7xl tracking-tight text-white max-w-5xl">
              {t("hero.title.1")}{" "}
              <span className="gold-shine italic">{t("hero.title.2")}</span>
            </h1>

            <p className="mt-7 sm:mt-8 text-base md:text-lg text-slate-300/90 max-w-2xl leading-relaxed">
              {t("hero.subtitle")}
            </p>

            <div className="mt-9 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
              <a
                href={waLink(t("wa.default"))}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-cta"
                className="group inline-flex items-center justify-center gap-3 bg-gold hover:bg-gold-light text-navy-900 font-semibold px-8 min-h-[56px] py-4 rounded-full transition-[color,background-color,border-color,box-shadow,transform] duration-300 hover:-translate-y-1 shadow-[0_10px_40px_-10px_rgba(201,162,75,0.6)]"
              >
                <FaWhatsapp className="h-5 w-5" />
                {t("hero.cta_whatsapp")}
                <span className="h-1.5 w-1.5 rounded-full bg-navy-900 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href={`tel:${BRAND.phoneRaw}`}
                data-testid="hero-call-cta"
                className="inline-flex items-center justify-center gap-3 border border-gold/50 hover:border-gold hover:bg-gold/5 text-gold font-medium px-8 min-h-[56px] py-4 rounded-full transition-[color,background-color,border-color,box-shadow,transform]"
              >
                <Phone className="h-4 w-4" />
                {BRAND.phone}
              </a>
              <Link
                to={OWNER_ROUTES[lang]}
                data-testid="hero-owner-cta"
                className="inline-flex items-center justify-center gap-2 text-sm text-slate-300 hover:text-gold hover:underline underline-offset-4 px-2 min-h-[56px] transition-colors"
              >
                <KeyRound className="h-4 w-4 text-gold" />
                {t("hero.owner_cta")}
              </Link>
            </div>

            {/* Trust strip */}
            <div className="mt-10 sm:mt-12 flex flex-wrap items-center gap-x-8 gap-y-4">
              <div className="flex items-center gap-2">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-gold text-gold" />
                  ))}
                </div>
                <span className="text-sm text-slate-400">
                  {t("hero.trust_stars")}
                </span>
              </div>
              <div className="h-5 w-px bg-gold/30 hidden md:block" />
              <div className="flex flex-wrap gap-2">
                {BRAND.languages.map((l, i) => (
                  <LangPill key={l} label={l} t={t} delay={0.6 + i * 0.08} />
                ))}
              </div>
            </div>
          </motion.div>

          {/* Right floating card */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="lg:col-span-4 relative hidden lg:block"
          >
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-gold/20 blur-3xl rounded-full" />
            <div className="relative bg-navy-800/70 backdrop-blur-xl border border-gold/20 rounded-3xl p-8 shadow-2xl">
              <div className="flex items-baseline gap-2">
                <Counter to={300} suffix="+" />
                <span className="text-sm text-slate-400">{t("hero.stats.families")}</span>
              </div>
              <div className="h-px bg-gold/20 my-6" />
              <div className="space-y-4">
                <Row label={t("hero.stats.time_label")} value={t("hero.stats.time_value")} />
                <Row label={t("hero.stats.docs_label")} value={t("hero.stats.docs_value")} />
                <Row label={t("hero.stats.lang_label")} value="4" />
                <Row
                  label={t("hero.stats.districts_label")}
                  value={t("hero.stats.districts_value")}
                />
              </div>
              <div className="h-px bg-gold/20 my-6" />
              <p className="text-xs text-slate-400 italic">
                {t("hero.stats.quote")}
              </p>
              <p className="text-xs text-gold mt-2">{t("hero.stats.quote_author")}</p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll cue */}
      <div
        aria-hidden
        className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-gold/70"
      >
        <span className="text-[10px] tracking-[0.4em] uppercase">{t("hero.scroll")}</span>
        <span className="relative h-10 w-px bg-gold/30 overflow-hidden">
          <span className="absolute inset-x-0 top-0 h-3 bg-gold animate-[scrollcue_1.6s_ease-in-out_infinite]" />
        </span>
      </div>
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-sm text-slate-400">{label}</span>
      <span className="text-sm text-white font-medium text-right">{value}</span>
    </div>
  );
}
